import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { tLang } from "../i18n.js";

const APPEAR_DELAY_MS = 3000;
const SUCCESS_DELAY_MS = 4000;
const INVALID_DELAY_MS = 4000;

function normalizePhone(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const keepPlus = trimmed.startsWith("+") ? "+" : "";
  const digits = trimmed.replace(/\D/g, "");

  return `${keepPlus}${digits}`;
}

function isValidPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 9 && digits.length <= 15;
}

export default function CallbackWidget({
  apiOrigin,
  hidden = false,
  endpoint = "/callback-request",
  variant = "glass",
  lang = "de",
  placeholder,
  submitText,
  successText,
}) {
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [showInlineError, setShowInlineError] = useState(false);
  const [showInlineSuccess, setShowInlineSuccess] = useState(false);

  const inputRef = useRef(null);
  const appearTimerRef = useRef(null);
  const successTimerRef = useRef(null);
  const invalidTimerRef = useRef(null);

  const ta = useCallback(
    (key, fallback) => tLang(key, lang, fallback),
    [lang]
  );

  const text = useMemo(() => {
    return {
      placeholder: placeholder || ta("landing.enter.number", "Telefonnummer"),
      submitText: submitText || ta("callback.submit", "Senden"),
      successText: successText || ta("landing.request", "Anfrage gesendet"),
      invalidPhone: ta(
        "callback.invalidPhone",
        "Bitte geben Sie eine gültige Telefonnummer ein."
      ),
      submitError: ta(
        "callback.submitError",
        "Fehler beim Senden. Bitte erneut versuchen."
      ),
    };
  }, [placeholder, submitText, successText, ta]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    appearTimerRef.current = window.setTimeout(() => {
      setIsVisible(true);
    }, APPEAR_DELAY_MS);

    return () => {
      window.clearTimeout(appearTimerRef.current);
      window.clearTimeout(successTimerRef.current);
      window.clearTimeout(invalidTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || isSuccess || showInlineError || showInlineSuccess) return;

    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus?.();
    }, 220);

    return () => window.clearTimeout(focusTimer);
  }, [isOpen, isSuccess, showInlineError, showInlineSuccess]);

  useEffect(() => {
    if (!hidden) return;

    setIsOpen(false);
    setError("");
    setShowInlineError(false);
    setShowInlineSuccess(false);

    window.clearTimeout(invalidTimerRef.current);
    window.clearTimeout(successTimerRef.current);
  }, [hidden]);

  const showPhoneValidationError = useCallback((message) => {
    window.clearTimeout(invalidTimerRef.current);

    setError(message);
    setShowInlineError(true);
    setShowInlineSuccess(false);
    setPhone("");

    invalidTimerRef.current = window.setTimeout(() => {
      setShowInlineError(false);
      setError("");

      window.setTimeout(() => {
        inputRef.current?.focus?.();
      }, 220);
    }, INVALID_DELAY_MS);
  }, []);

  const showPhoneSuccess = useCallback((message) => {
    window.clearTimeout(successTimerRef.current);

    setError(message);
    setShowInlineSuccess(true);
    setShowInlineError(false);
    setIsSuccess(true);
    setPhone("");

    successTimerRef.current = window.setTimeout(() => {
      setShowInlineSuccess(false);
      setError("");
      setIsOpen(false);
      setIsSuccess(false);
    }, SUCCESS_DELAY_MS);
  }, []);

  const toggleOpen = useCallback(() => {
    if (!isVisible || hidden || isSending) return;
    if (isSuccess) return;

    window.clearTimeout(invalidTimerRef.current);
    window.clearTimeout(successTimerRef.current);

    setError("");
    setShowInlineError(false);
    setShowInlineSuccess(false);
    setIsOpen((prev) => !prev);
  }, [hidden, isSending, isSuccess, isVisible]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (isSending || showInlineError || showInlineSuccess) return;

      const normalizedPhone = normalizePhone(phone);

      if (!isValidPhone(normalizedPhone)) {
        showPhoneValidationError(text.invalidPhone);
        return;
      }

      setIsSending(true);
      setError("");

      try {
        const res = await fetch(`${apiOrigin}${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phone: normalizedPhone,
            page: window.location.href,
            sentAt: new Date().toISOString(),
            lang,
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || data?.ok === false) {
          throw new Error(data?.message || "Request failed");
        }

        showPhoneSuccess(text.successText);
      } catch (err) {
        console.error("CallbackWidget submit error:", err);
        showPhoneValidationError(text.submitError);
      } finally {
        setIsSending(false);
      }
    },
    [
      apiOrigin,
      endpoint,
      isSending,
      phone,
      text,
      lang,
      showInlineError,
      showInlineSuccess,
      showPhoneValidationError,
      showPhoneSuccess,
    ]
  );

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className={[
        "callback-widget",
        `callback-widget--${variant}`,
        isVisible && !hidden ? "is-visible" : "",
        isOpen ? "is-open" : "",
        isSuccess ? "is-success" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        position: "fixed",
        right: "18px",
        bottom: "18px",
        zIndex: 2147483000,
      }}
      aria-live="polite"
    >
      <button
        type="button"
        className="callback-widget__launcher"
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-label="Callback"
      >
        <span className="callback-widget__pulse" aria-hidden="true" />
      </button>

      <div className="callback-widget__surface">
        <div className="callback-widget__content">
          <form
            className="callback-widget__form"
            onSubmit={handleSubmit}
            noValidate
          >
            <div
              className={[
                "callback-widget__input-wrap",
                showInlineError ? "is-error-mode" : "",
                showInlineSuccess ? "is-success-mode" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="callback-widget__input-stage callback-widget__input-stage--field">
                <input
                  ref={inputRef}
                  className="callback-widget__input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={text.placeholder}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);

                    if (error && !showInlineError && !showInlineSuccess) {
                      setError("");
                    }
                  }}
                />
              </div>

              <div className="callback-widget__input-stage callback-widget__input-stage--error">
                <div
                  className={[
                    "callback-widget__inline-error-message",
                    showInlineSuccess ? "is-success" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {showInlineSuccess ? text.successText : error}
                </div>
              </div>
            </div>

            <button
              className="callback-widget__submit"
              type="submit"
              disabled={isSending || showInlineError || showInlineSuccess}
              aria-label={text.submitText}
            />
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}